import type { KickFlow } from "../client";
import type { ChatMessageRequest, ChatMessageResponse, WebSocketConfig } from "../types";
import { KickWebSocket } from "../common/websocket";
import { getChatroomId } from "../common/api";

/**
 * Chat module for interacting with Kick.com chat API
 * Handles sending messages, managing chat interactions, and WebSocket connections
 */
export class ChatModule {
	private wsConnections: Map<number, KickWebSocket> = new Map();

	constructor(private client: KickFlow) {}

	/**
	 * Send a message to a channel's chat
	 * 
	 * @param message - The message to send (bot or user message)
	 * @returns Promise resolving to the message response
	 * 
	 * @example
	 * ```typescript
	 * // Send a bot message
	 * await client.chat.postMessage({
	 *   type: "bot",
	 *   content: "Hello from bot!",
	 * });
	 * 
	 * // Send a user message
	 * await client.chat.postMessage({
	 *   type: "user",
	 *   broadcaster_user_id: 123456,
	 *   content: "Hello from user!",
	 * });
	 * ```
	 */
	async postMessage(message: ChatMessageRequest): Promise<ChatMessageResponse> {
		if (message.type === "bot") {
			return this.client.request<ChatMessageResponse>("/chat/messages", {
				method: "POST",
				body: JSON.stringify({
					content: message.content,
					reply_to_message_id: message.reply_to_message_id,
				}),
			});
		} else {
			return this.client.request<ChatMessageResponse>("/chat/messages", {
				method: "POST",
				body: JSON.stringify({
					broadcaster_user_id: message.broadcaster_user_id,
					content: message.content,
					reply_to_message_id: message.reply_to_message_id,
				}),
			});
		}
	}

	/**
	 * Reply to a specific message in chat
	 * 
	 * @param messageId - The ID of the message to reply to
	 * @param content - The content of the reply
	 * @param broadcasterUserId - Required for user messages
	 * @returns Promise resolving to the message response
	 */
	async replyToMessage(
		messageId: string,
		content: string,
		broadcasterUserId?: number
	): Promise<ChatMessageResponse> {
		const message: ChatMessageRequest = broadcasterUserId
			? {
					type: "user",
					broadcaster_user_id: broadcasterUserId,
					content,
					reply_to_message_id: messageId,
			}
			: {
					type: "bot",
					content,
					reply_to_message_id: messageId,
			};

		return this.postMessage(message);
	}

	/**
	 * Connect to a channel's chat using channel name
	 * 
	 * @param channelName - The channel name (e.g., "xqc", "amouranth") 
	 * @param config - WebSocket configuration options
	 * @returns Promise<KickWebSocket> instance for the chatroom
	 * 
	 * @example
	 * ```typescript
	 * const chatWS = await client.chat.connectToChatByChannel("xqc", {
	 *   autoReconnect: true,
	 *   debug: true
	 * });
	 * 
	 * chatWS.on('message', (message) => {
	 *   console.log(`${message.sender.username}: ${message.content}`);
	 * });
	 * 
	 * chatWS.on('user_joined', (user) => {
	 *   console.log(`${user.username} joined the chat`);
	 * });
	 * ```
	 */
	async connectToChatByChannel(
		channelName: string,
		config: Omit<WebSocketConfig, 'chatroomId'> = {}
	): Promise<KickWebSocket> {
		// Get chatroom ID from channel name
		const chatroomId = await getChatroomId(channelName);
		
		if (!chatroomId) {
			throw new Error(`Could not find chatroom for channel: ${channelName}`);
		}

		return this.connectToChat(chatroomId, config);
	}

	/**
	 * Connect to a chatroom's WebSocket for real-time events
	 * 
	 * @param chatroomId - The ID of the chatroom to connect to
	 * @param config - WebSocket configuration options
	 * @returns KickWebSocket instance for the chatroom
	 * 
	 * @example
	 * ```typescript
	 * const chatWS = client.chat.connectToChat(12345, {
	 *   autoReconnect: true,
	 *   debug: true
	 * });
	 * 
	 * chatWS.on('message', (message) => {
	 *   console.log(`${message.sender.username}: ${message.content}`);
	 * });
	 * 
	 * chatWS.on('user_joined', (user) => {
	 *   console.log(`${user.username} joined the chat`);
	 * });
	 * 
	 * await chatWS.connect();
	 * ```
	 */
	connectToChat(
		chatroomId: number, 
		config: Omit<WebSocketConfig, 'chatroomId'> = {}
	): KickWebSocket {
		// Check if already connected to this chatroom
		const existing = this.wsConnections.get(chatroomId);
		if (existing && existing.isConnected()) {
			return existing;
		}

		// Create new WebSocket connection
		const wsConfig: WebSocketConfig = {
			chatroomId,
			...config
		};

		const ws = new KickWebSocket(wsConfig);

		// Store connection
		this.wsConnections.set(chatroomId, ws);

		// Clean up on disconnect
		ws.on('disconnect', () => {
			this.wsConnections.delete(chatroomId);
		});

		return ws;
	}

	/**
	 * Disconnect from a specific chatroom's WebSocket
	 * 
	 * @param chatroomId - The ID of the chatroom to disconnect from
	 * 
	 * @example
	 * ```typescript
	 * client.chat.disconnectFromChat(12345);
	 * ```
	 */
	disconnectFromChat(chatroomId: number): void {
		const ws = this.wsConnections.get(chatroomId);
		if (ws) {
			ws.disconnect();
			this.wsConnections.delete(chatroomId);
		}
	}

	/**
	 * Disconnect from all chatroom WebSockets
	 * 
	 * @example
	 * ```typescript
	 * client.chat.disconnectFromAllChats();
	 * ```
	 */
	disconnectFromAllChats(): void {
		for (const [chatroomId, ws] of this.wsConnections) {
			ws.disconnect();
		}
		this.wsConnections.clear();
	}

	/**
	 * Get WebSocket connection for a specific chatroom
	 * 
	 * @param chatroomId - The ID of the chatroom
	 * @returns WebSocket instance or undefined if not connected
	 * 
	 * @example
	 * ```typescript
	 * const ws = client.chat.getChatConnection(12345);
	 * if (ws && ws.isConnected()) {
	 *   console.log('Connected to chat');
	 * }
	 * ```
	 */
	getChatConnection(chatroomId: number): KickWebSocket | undefined {
		return this.wsConnections.get(chatroomId);
	}

	/**
	 * Get all active WebSocket connections
	 * 
	 * @returns Map of chatroom IDs to WebSocket instances
	 * 
	 * @example
	 * ```typescript
	 * const connections = client.chat.getAllChatConnections();
	 * for (const [chatroomId, ws] of connections) {
	 *   console.log(`Chatroom ${chatroomId}: ${ws.getState()}`);
	 * }
	 * ```
	 */
	getAllChatConnections(): Map<number, KickWebSocket> {
		return new Map(this.wsConnections);
	}
}